import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { TaskTemplate, TaskType } from '@ndvr/customer-api';

import { MinimalTiptapEditor } from '@/components/minimal-tiptap';
import { Button } from '@/components/ui/button';
import { ComboboxOption, FormCombobox } from '@/components/ui/combobox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { FormInput } from '@/components/ui/input';
import { FormSelect, SelectOption } from '@/components/ui/select';
import { convertPlainTextToHtml, hasVisibleTextContent, logger, titleCase } from 'utils';
import { useTenantAdvisors } from 'views/admin/tenantTeam/tenant-team-queries';
import { useAuthContext } from 'views/auth/Auth';
import { Toast } from 'widgets';

import { useCreateTemplate, useUpdateTemplate } from '../task-templates-queries';

export interface CreateUpdateTemplateDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    template?: TaskTemplate | null;
}

type TemplateFormValues = {
    title: string;
    description: string;
    assigneeOption?: ComboboxOption;
    taskTypeOption: SelectOption;
};

const TASK_TYPE_OPTIONS: SelectOption[] = Object.values(TaskType)
    .map((type) => ({ value: type, label: titleCase(type) }))
    .sort((a, b) => a.label.localeCompare(b.label));

export const CreateUpdateTemplateDialog: React.FC<CreateUpdateTemplateDialogProps> = ({
    open,
    onOpenChange,
    template,
}: CreateUpdateTemplateDialogProps) => {
    const isEdit = !!template?.taskTemplateGUID;
    const { user } = useAuthContext();
    const { advisors, isPending: advisorsLoading } = useTenantAdvisors();

    const defaultAssigneeOption = useMemo<ComboboxOption | undefined>(() => {
        const advisorGuid = template?.defaultAssignee || user?.personOrgGUID;
        if (!advisorGuid) return undefined;
        const advisor = advisors.find((u) => u.internalUserGUID === advisorGuid);
        if (advisor) {
            return {
                value: advisor.internalUserGUID,
                label: advisor.name,
            };
        }
        if (!advisorsLoading && advisors.length > 0) {
            logger.error(
                "Couldn't match advisor. The defaultAssignee value in the task template doesn't match any advisor",
                { advisorGuid, advisorsCount: advisors.length },
            );
        }
        return { value: advisorGuid, label: 'Unknown Default Task Template Assignee' };
    }, [template?.defaultAssignee, advisors, advisorsLoading, user?.personOrgGUID]);

    const form = useForm<TemplateFormValues>({
        defaultValues: {
            title: template?.title || '',
            description: template?.description || '',
            assigneeOption: defaultAssigneeOption,
            taskTypeOption: {
                value: template?.type || TaskType.GENERAL,
                label: titleCase(template?.type || titleCase(TaskType.GENERAL)),
            },
        },
        mode: 'onChange',
    });

    // When switching between create/edit or changing the template, reset form fields
    useEffect(() => {
        if (!open) return;
        form.reset(
            {
                title: template?.title || '',
                description: template?.description || '',
                assigneeOption: defaultAssigneeOption,
                taskTypeOption: {
                    value: template?.type || TaskType.GENERAL,
                    label: titleCase(template?.type || TaskType.GENERAL),
                },
            },
            { keepDirty: false },
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, template?.taskTemplateGUID, defaultAssigneeOption?.value]);

    // Keep defaultAssigneeOption in sync once advisor users load
    useEffect(() => {
        if (!form.getValues('assigneeOption') && defaultAssigneeOption) {
            form.setValue('assigneeOption', defaultAssigneeOption, { shouldDirty: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultAssigneeOption?.value]);

    const createMutation = useCreateTemplate();
    const updateMutation = useUpdateTemplate();

    const onSubmit = async (data: TemplateFormValues) => {
        const advisorPersonOrgGUID = user?.personOrgGUID;

        if (!advisorPersonOrgGUID) {
            Toast.forError(new Error('Missing advisor GUID'), 'Unable to save template');
            return;
        }

        try {
            if (isEdit && template?.taskTemplateGUID) {
                await updateMutation.mutateAsync({
                    taskTemplateGUID: template.taskTemplateGUID as unknown as string,
                    advisorPersonOrgGUID,
                    defaultAssignee: data.assigneeOption!.value,
                    title: data.title,
                    description: data.description,
                    type: (data.taskTypeOption.value as TaskType) || TaskType.GENERAL,
                });
                Toast.success('Template updated');
            } else {
                await createMutation.mutateAsync({
                    advisorPersonOrgGUID,
                    defaultAssignee: data.assigneeOption!.value,
                    title: data.title,
                    description: data.description,
                    type: (data.taskTypeOption.value as TaskType) || TaskType.GENERAL,
                });
                Toast.success('Template created');
            }
            onOpenChange(false);
        } catch (e: any) {
            Toast.forError(e, isEdit ? 'Failed to update template' : 'Failed to create template');
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>{isEdit ? 'Edit Template' : 'Create Template'}</DialogTitle>
                </DialogHeader>

                <Form {...form}>
                    <form className="flex flex-col gap-3" onSubmit={form.handleSubmit(onSubmit)}>
                        <FormInput
                            formMethods={form}
                            name="title"
                            label="Template Title*"
                            placeholder="Enter template title"
                            required
                        />
                        <FormField
                            control={form.control}
                            name="description"
                            rules={{
                                validate: (value) =>
                                    hasVisibleTextContent(value || '') || 'Template description is required',
                            }}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Template Description*</FormLabel>
                                    <FormControl className="mt-3">
                                        <MinimalTiptapEditor
                                            value={convertPlainTextToHtml(field.value || '')}
                                            onChange={field.onChange}
                                            className="w-full"
                                            editorContentClassName="p-5"
                                            output="html"
                                            placeholder="Enter template description"
                                            autofocus={true}
                                            editable={true}
                                            editorClassName="focus:outline-hidden"
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormCombobox
                            formMethods={form}
                            name="assigneeOption"
                            options={advisors.map((advisor) => ({
                                value: advisor.internalUserGUID,
                                label: advisor.name,
                            }))}
                            label="Default Assignee*"
                            selectOptionLabel="Select an assignee"
                            noOptionsLabel="No assignees found"
                            placeholder="Select an assignee"
                            required
                        />
                        <FormSelect
                            formMethods={form}
                            name="taskTypeOption"
                            options={TASK_TYPE_OPTIONS}
                            label="Task Type"
                            placeholder="Select a task type"
                        />

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={createMutation.isPending || updateMutation.isPending}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                                {createMutation.isPending || updateMutation.isPending
                                    ? isEdit
                                        ? 'Saving...'
                                        : 'Creating...'
                                    : isEdit
                                      ? 'Save Changes'
                                      : 'Create Template'}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};

export default CreateUpdateTemplateDialog;
